import * as React from 'react';
import {
  Button, Stack, Typography, 
  ToggleButtonGroup, ToggleButton, Box, 
  CircularProgress
} from '@mui/material';
import { myConstants } from '../../shares/Constants';
import { useFetchTournamentQuery } from '../../store/tournament/TournamentApi';
import NumberSpinner from '../../shares/NumberSpinner';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
//import { ConstructionOutlined } from '@mui/icons-material';


// Standardvorgabe, sofern noch nichts vorhanden ist 
// (für die Neuanlage eines Teams)
const DEFAULTS = 
{
  players                   : 4,
  fellows                   : 0,
  vacationType              : myConstants.overnight,
  firstDay                  : myConstants.fd_saturday,
  saturdayBreakfast         : 0,
  sundayBreakfast           : 0,
  saturdayLunch             : 0,
  barSets                   : 0,
  shuttleArrivalPassengers  : 0,
  shuttleDeparturePassengers: 0,
};


export function TeamForm(
  {
    year, 
    club, 
    initialValues = {}, 
    onSubmit, 
    isLoading, 
    onCancel, 
    submitLabel = 'Speichern' 
  }
)
{

  // Verbinden der vorgegebenen und der Initialisierungswerte
  const mergedValues = {...DEFAULTS, ...initialValues};

  // Einlesen des aktuellen Turniers
  const { data : tournament, isLoading : isTournamentLoading } = useFetchTournamentQuery(year);

  // Vorbereiten der Setter und Getter für die Statusverarbeitung jedes Feldes
  const [players,                 setPlayers]                   = React.useState(mergedValues.players);
  const [fellows,                   setFellows]                   = React.useState(mergedValues.fellows);
  const [vacationType,              setVacationType]              = React.useState(mergedValues.vacationType);
  const [firstDay,                  setFirstDay]                  = React.useState(mergedValues.firstDay);
  const [saturdayBreakfast,         setSaturdayBreakfast]         = React.useState(mergedValues.saturdayBreakfast);
  const [sundayBreakfast,           setSundayBreakfast]           = React.useState(mergedValues.sundayBreakfast);
  const [saturdayLunch,             setSaturdayLunch]             = React.useState(mergedValues.saturdayLunch);
  const [barSets,                   setBarSets]                   = React.useState(mergedValues.barSets);
  const [shuttleArrivalPassengers,  setShuttleArrivalPassengers]  = React.useState(mergedValues.shuttleArrivalPassengers);
  const [shuttleDeparturePassengers,setShuttleDeparturePassengers]= React.useState(mergedValues.shuttleDeparturePassengers);
  const [shuttleArrivalDate,        setShuttleArrivalDate]        = React.useState(
    initialValues.shuttleArrivalDate ? dayjs(initialValues.shuttleArrivalDate) : dayjs()
  );
  const [shuttleDepartureDate,      setShuttleDepartureDate]      = React.useState(
    initialValues.shuttleDepartureDate ? dayjs(initialValues.shuttleDepartureDate) : dayjs()
  );

  // Verarbeiten des Submits - hier in der Form, dass es sowohl 
  // zum Speichern, als auch zum Erstellen ist.  
  const handleSubmit = async (event) => 
  {
    event.preventDefault();
    await onSubmit(
      {
        players:               parseInt(players),
        fellows:               parseInt(fellows),
        vacationType,
        firstDay,
        saturdayBreakfast:     parseInt(saturdayBreakfast),
        sundayBreakfast:       parseInt(sundayBreakfast),
        fridayLunch:           0,
        saturdayLunch:         parseInt(saturdayLunch),
        barSets:               parseInt(barSets),
        shuttleArrivingCount:  parseInt(shuttleArrivalPassengers),
        shuttleArrivingDate:   shuttleArrivalDate.toISOString(),
        shuttleDepartingCount: parseInt(shuttleDeparturePassengers),
        shuttleDepartingDate:  shuttleDepartureDate.toISOString(),
      }      
    );
  };


  // Berechnen der noch vorhandenen Anzahl der Biersetgarnituren
  const barSetsAvailable = (tournament?.[0]?.bar_sets_available != null)
    ? tournament[0].bar_sets_available + (initialValues.barSets ?? 0)
    : undefined;
  

  return(
    <Stack direction="column" spacing={2} component="form" onSubmit={handleSubmit}>
      <NumberSpinner
        label="Anzahl SpielerInnen"
        min={4} size="small"
        value={players}
        onValueChange={(v) => setPlayers(v ?? 0)}
      />
      <NumberSpinner
        label="Anzahl Mitreisende"
        size="small"
        value={fellows}
        onValueChange={(v) => setFellows(v ?? 0)}
      />

      <Typography variant="h7">Art des Besuchs</Typography>
      <ToggleButtonGroup value={vacationType} exclusive onChange={(e) => setVacationType(e.target.value)}>
        <ToggleButton value={myConstants.saturday}>Tagesgäste (Samstag)</ToggleButton>
        <ToggleButton value={myConstants.weekend}>Wochenendgäste (Samstag und Sonntag)</ToggleButton>
        <ToggleButton value={myConstants.overnight}>Übernachtungsgäste (Wochenende und Zelten)</ToggleButton>
      </ToggleButtonGroup>

      {vacationType === myConstants.overnight && <>
        <Typography variant="h7">Tag der Anreise</Typography>
        <ToggleButtonGroup value={firstDay} exclusive onChange={(e) => setFirstDay(e.target.value)}>
          <ToggleButton value={myConstants.fd_friday}>Freitagabend</ToggleButton>
          <ToggleButton value={myConstants.fd_saturday}>Samstagmorgen</ToggleButton>
        </ToggleButtonGroup>
      </>}

      {vacationType === myConstants.overnight && firstDay === myConstants.fd_friday &&
        <NumberSpinner
          label="Anzahl Frühstück am Samstag"
          min={0} size="small"
          value={saturdayBreakfast}
          onValueChange={(value) => setSaturdayBreakfast(value ?? 0)}
        />
      }
      <Box>
        <NumberSpinner
          label="Anzahl Abendessen am Samstag"
          min={0} size="small"
          value={saturdayLunch}
          onValueChange={(value) => setSaturdayLunch(value ?? 0)}
        />
        <Tooltip title={`Wir bieten ein Pasta-Buffet bei unserem Clubwirt für EUR ${
          isTournamentLoading || !club ? '...' :
          club.is_local
            ? parseFloat(tournament?.[0]?.fee_lunch_local).toFixed(2)
            : parseFloat(tournament?.[0]?.fee_lunch).toFixed(2)
        } an.`} placement="right" arrow>
          <IconButton color="primary"><HelpOutlineIcon /></IconButton>
        </Tooltip>
      </Box>

      <Box>
        <NumberSpinner
          label="Anzahl Frühstück am Sonntag"
          min={0} size="small"
          value={sundayBreakfast}
          onValueChange={(v) => setSundayBreakfast(v ?? 0)}
        />
        <Tooltip title={`Wir bieten ein einfaches Frühstück für EUR ${
          isTournamentLoading || !club ? '...' :
          club.is_local
            ? parseFloat(tournament?.[0]?.fee_breakfast_local).toFixed(2)
            : parseFloat(tournament?.[0]?.fee_breakfast).toFixed(2)
        } an.`} placement="right" arrow>
          <IconButton color="primary"><HelpOutlineIcon /></IconButton>
        </Tooltip>
      </Box>

      <Box>
        <NumberSpinner
          label="Reservierung Bierbank-Garnituren"
          size="small"
          value={barSets}
          onValueChange={(value) => setBarSets(value ?? 0)}
        />
        <Tooltip title={`Wir bieten die Bierset-Garnituren zum Preis von EUR ${
            isTournamentLoading || !club ? '...' :
            club.is_local
              ? parseFloat(tournament?.[0]?.fee_bar_set_local).toFixed(2)
              : parseFloat(tournament?.[0]?.fee_bar_set).toFixed(2)
            } zum Mieten an. So lange der Vorrat reicht (aktuell noch verfügbar ${
              barSetsAvailable
            }).
          `} placement="right" arrow>
          <IconButton color="primary"><HelpOutlineIcon /></IconButton>
        </Tooltip>
      </Box>

      <Box>
        <NumberSpinner
          label="Benötigte Shuttleplätze bei Ankunft"
          size="small"
          value={shuttleArrivalPassengers}
          onValueChange={(v) => setShuttleArrivalPassengers(v ?? 0)}
        />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateTimePicker
            label="Ankunft am Bahnhof"
            value={shuttleArrivalDate}
            onChange={(v) => setShuttleArrivalDate(v)}
            disabled={shuttleArrivalPassengers === 0}
          />
        </LocalizationProvider>
      </Box>

      <Box>
        <NumberSpinner
          label="Benötigte Shuttleplätze bei Abfahrt"
          size="small"
          value={shuttleDeparturePassengers}
          onValueChange={(v) => setShuttleDeparturePassengers(v ?? 0)}
        />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateTimePicker
            label="Abfahrt am Bahnhof"
            value={shuttleDepartureDate}
            onChange={(v) => setShuttleDepartureDate(v)}
            disabled={shuttleDeparturePassengers === 0}
          />
        </LocalizationProvider>
      </Box>

      <Stack direction="row" spacing={2}>
        <Button type="submit" variant="contained" disabled={isLoading}>
          {isLoading ? <CircularProgress size={20} /> : submitLabel}
        </Button>
        {onCancel && (
          <Button variant="outlined" onClick={onCancel} disabled={isLoading}>
            Abbrechen
          </Button>
        )}
      </Stack>
    </Stack>
  );
}